/**
 * File: pipelineUtils.ts
 * Path: apps/admin/src/shared/utils/pipelineUtils.ts
 * Description: Filtering, grouping and totals for the pipeline board and table.
 */

import {
  isFollowUpDue,
  LEAD_STAGES,
  OPEN_LEAD_STAGES,
  type Lead,
  type LeadSource,
  type LeadStage,
} from "@idk/config";

export interface PipelineFilters {
  /** Matches name, contact, phone, email, area and niche. */
  text: string;
  stage: LeadStage | "open" | "all";
  source: LeadSource | "all";
  followUpDueOnly: boolean;
  /** Parked leads are hidden; this shows only them instead. */
  parkedOnly: boolean;
  /** Only open leads that have gone quiet for longer than their stage allows. */
  staleOnly: boolean;
}

export const DEFAULT_PIPELINE_FILTERS: PipelineFilters = {
  text: "",
  stage: "open",
  source: "all",
  followUpDueOnly: false,
  parkedOnly: false,
  staleOnly: false,
};

const haystack = (lead: Lead) =>
  [lead.name, lead.contactName, lead.phone, lead.email, lead.area, lead.niche]
    .join(" ")
    .toLowerCase();

export const filterLeads = (
  leads: Lead[],
  filters: PipelineFilters,
  today?: string,
  /** Ids of the leads going stale, for the stale quick view. */
  staleIds: ReadonlySet<string> = new Set(),
): Lead[] => {
  const words = filters.text.toLowerCase().split(/\s+/).filter(Boolean);
  return leads.filter((lead) => {
    if (lead.parked !== filters.parkedOnly) return false;
    if (filters.stage === "open" && !OPEN_LEAD_STAGES.includes(lead.stage)) return false;
    if (filters.stage !== "open" && filters.stage !== "all" && lead.stage !== filters.stage) {
      return false;
    }
    if (filters.source !== "all" && lead.source !== filters.source) return false;
    if (filters.followUpDueOnly && !isFollowUpDue(lead, today)) return false;
    if (filters.staleOnly && !staleIds.has(lead.id)) return false;
    if (words.length) {
      const text = haystack(lead);
      if (!words.every((w) => text.includes(w))) return false;
    }
    return true;
  });
};

/** Leads per stage, in pipeline order, every stage present even when empty. */
export const groupByStage = (leads: Lead[]): Record<LeadStage, Lead[]> => {
  const groups = Object.fromEntries(LEAD_STAGES.map((s) => [s, [] as Lead[]])) as Record<
    LeadStage,
    Lead[]
  >;
  for (const lead of leads) groups[lead.stage].push(lead);
  return groups;
};

export interface PipelineSummary {
  /** Open leads, not counting parked ones. */
  open: number;
  parked: number;
  followUpsDue: number;
  /** Sum of estimated values on open leads, in rand. */
  openValue: number;
  wonThisMonth: number;
  /** Sum of estimated values on the leads won this month, in rand. */
  wonValue: number;
}

export const summarisePipeline = (
  leads: Lead[],
  today: string,
): PipelineSummary => {
  const month = today.slice(0, 7);
  const monthOf = (ms: number | null) =>
    ms === null
      ? ""
      : new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" })
          .format(new Date(ms))
          .slice(0, 7);

  let open = 0;
  let parked = 0;
  let followUpsDue = 0;
  let openValue = 0;
  let wonThisMonth = 0;
  let wonValue = 0;
  for (const lead of leads) {
    if (lead.parked) {
      parked += 1;
      continue;
    }
    if (OPEN_LEAD_STAGES.includes(lead.stage)) {
      open += 1;
      openValue += lead.value ?? 0;
    }
    if (isFollowUpDue(lead, today)) followUpsDue += 1;
    if (lead.stage === "won" && monthOf(lead.stageChangedAt) === month) {
      wonThisMonth += 1;
      wonValue += lead.value ?? 0;
    }
  }
  return { open, parked, followUpsDue, openValue, wonThisMonth, wonValue };
};
